import React, { useState, useEffect } from 'react';
import client from '../api/client';

const SELECTOR_TYPES = {
    lpTracking: {
        endpoint: 'lp-tracking',
        field: 'include_in_lp_tracking',
        title: 'LP characters',
        sub: 'Characters whose LP balances are tracked.',
        emptyHint: 'Select at least one character to start tracking LP.',
    },
    marketSeeding: {
        endpoint: 'market-seeding',
        field: 'include_in_market_seeding',
        title: 'Market characters',
        sub: 'Orders and wallet history from these characters count toward stock.',
        emptyHint: 'Select at least one character to fetch market data.',
    },
    investments: {
        endpoint: 'investments',
        field: 'include_in_investments',
        title: 'Investment characters',
        sub: 'Their Jita 4-4 hangars, Jita sell orders and Jita/Perimeter buys make up your holdings.',
        emptyHint: 'Select the characters whose Jita hangars hold your investments.',
    },
};

/**
 * Character selector shared by LP tracking, market seeding and investments.
 * trackingType selects which inclusion flag and endpoint to drive.
 */
function CharacterSelector({ trackingType, onCharactersUpdated }) {
    const { endpoint, field, title, sub, emptyHint } = SELECTOR_TYPES[trackingType];

    const [characters, setCharacters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState({});

    useEffect(() => {
        fetchCharacters();
    }, []);

    const fetchCharacters = async () => {
        try {
            setLoading(true);
            const response = await client.get('/auth/characters/');
            setCharacters(response.data.characters || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching characters:', err);
            setError('Failed to load characters');
        } finally {
            setLoading(false);
        }
    };

    const toggleCharacter = async (characterId, currentValue) => {
        setUpdating(prev => ({ ...prev, [characterId]: true }));

        try {
            await client.patch(`/auth/characters/${characterId}/${endpoint}/`, {
                [field]: !currentValue
            });

            // Update local state
            setCharacters(prev =>
                prev.map(char =>
                    char.character_id === characterId
                        ? { ...char, [field]: !currentValue }
                        : char
                )
            );

            // Notify parent component
            if (onCharactersUpdated) {
                onCharactersUpdated();
            }
        } catch (err) {
            console.error('Error toggling character:', err);
            alert('Failed to update character selection');
        } finally {
            setUpdating(prev => ({ ...prev, [characterId]: false }));
        }
    };

    const selectAll = () => {
        characters.forEach(char => {
            if (!char[field]) {
                toggleCharacter(char.character_id, false);
            }
        });
    };

    const deselectAll = () => {
        characters.forEach(char => {
            if (char[field]) {
                toggleCharacter(char.character_id, true);
            }
        });
    };

    const selectedCount = characters.filter(c => c[field]).length;

    return (
        <section className="panel">
            <div className="panel-head">
                <h2>{title}</h2>
                <span className="panel-sub">{sub}</span>
                {characters.length > 0 && (
                    <div className="panel-actions">
                        <span className="panel-sub">{selectedCount} of {characters.length} selected</span>
                        <button onClick={selectAll} className="btn btn-ghost btn-sm">Select all</button>
                        <button onClick={deselectAll} className="btn btn-ghost btn-sm">Clear</button>
                    </div>
                )}
            </div>
            <div className="panel-body char-chips">
                {loading && <span className="text-dim">Loading characters...</span>}
                {error && <span className="text-neg">{error}</span>}
                {!loading && !error && characters.length === 0 && (
                    <span className="text-dim">No characters yet. Add one in Account Settings.</span>
                )}
                {characters.map(character => (
                    <label
                        key={character.character_id}
                        className={`char-chip${character[field] ? ' selected' : ''}${updating[character.character_id] ? ' busy' : ''}`}
                    >
                        <input
                            type="checkbox"
                            checked={character[field] || false}
                            onChange={() => toggleCharacter(character.character_id, character[field])}
                            disabled={updating[character.character_id]}
                        />
                        {character.portrait_64x64 && (
                            <img src={character.portrait_64x64} alt="" className="char-chip-portrait" />
                        )}
                        <span>
                            <b>{character.character_name}</b>
                            <small>
                                {character.corporation_name}
                                {character.is_primary && ' · primary'}
                            </small>
                        </span>
                    </label>
                ))}
                {!loading && characters.length > 0 && selectedCount === 0 && (
                    <span className="text-warn">{emptyHint}</span>
                )}
            </div>
        </section>
    );
}

export default CharacterSelector;
